/*
 * Copyright 2026 Sony Corporation
 */

import AddIcon from '@mui/icons-material/Add';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppHeader } from '../components/AppHeader';
import { useAuth } from '../contexts/auth-context';
import { useWorlds } from '../hooks/use-world';

/**
 * The scene as it was last saved, or a stand-in.
 *
 * The box is sized by padding rather than by `aspect-ratio`, because a grid row
 * takes its height from the item's intrinsic one and an image has none until it
 * has loaded — which would collapse every card to a line of text and back.
 *
 * `onError` covers the one case the API cannot: a signed URL that expired while
 * the page was left open. A broken-image glyph is worse than no picture.
 */
const WorldThumbnail = ({ url }: { url?: string }) => {
  const [failed, setFailed] = useState(false);

  return (
    <Box className="relative h-0 w-full pb-[56.25%]" sx={{ bgcolor: 'action.hover' }}>
      {url && !failed ? (
        <CardMedia
          component="img"
          image={url}
          alt=""
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <Box className="absolute inset-0 flex items-center justify-center">
          <ViewInArIcon fontSize="large" sx={{ color: 'text.disabled' }} />
        </Box>
      )}
    </Box>
  );
};

export const WorldList = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { data: worlds, error, loading, reload } = useWorlds();

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string>();

  const create = async () => {
    setBusy(true);
    setCreateError(undefined);
    try {
      const world = await api.createWorld({ name });
      setCreating(false);
      setName('');
      navigate(`/worlds/${world.world_id}`);
    } catch (cause) {
      setCreateError(cause instanceof Error ? cause.message : 'Could not create the world.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box className="min-h-screen">
      <AppHeader title="Worlds" />

      <Box component="main" className="mx-auto max-w-5xl p-6">
        <Box className="mb-4 flex items-center justify-between">
          <Typography variant="h6" component="h2">
            Hello, device worlds
          </Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreating(true)}>
            New world
          </Button>
        </Box>

        {error && (
          <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>
            {error.message}
          </Alert>
        )}

        {loading && !worlds && <CircularProgress />}

        {worlds?.length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" gutterBottom>
                No worlds yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A world holds the devices of one lab setup, and the gateway that runs them.
              </Typography>
            </CardContent>
          </Card>
        )}

        <Box className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {worlds?.map((world) => (
            <Card key={world.world_id}>
              <CardActionArea onClick={() => navigate(`/worlds/${world.world_id}`)}>
                <WorldThumbnail url={world.thumbnail_url} />
                <CardContent>
                  <Typography variant="subtitle1">{world.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Created {new Date(world.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Box>

      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="xs">
        <DialogTitle>New world</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" className="mb-4">
              {createError}
            </Alert>
          )}
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button onClick={create} variant="contained" disabled={busy || name.trim() === ''}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
